import React, { useEffect, useState, useRef } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  CheckSquare,
  Calendar,
  Clock,
  Package,
  BarChart3,
  Mail,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import liriworksImage from "../assets/hrm-image.png";

import { AxiosResponse } from "axios";
import appConfig from "services/apiConfig";
import { storageService } from "services/storageService";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import {
  getPostLoginRedirectPath,
  logAuthPayload,
} from "util/moduleAccess";
import { isValidEmail } from "util/validation";
import { preventSpaceInput, removeSpacesFromInput } from "util/preventSpecialCharInput";
// import { setFeaturesEnabled } from "util/featuresUtil";

// ---------------- Validation ----------------
const validationSchema = yup.object({
  email: yup
    .string()
    .required("Email is required")
    .test(
      "is-valid-email",
      "Please enter a valid email address",
      (value) => !value || isValidEmail(value)
    ),
  password: yup
    .string()
    .required("Password is required"),
});

type FormData = yup.InferType<typeof validationSchema>;

interface LoginSuccessResponse {
  message?: string;
  accessToken?: string;
  refreshToken?: string;
  subdomain?: string;
  subDomain?: string; // backend may send either casing
}

interface LoginMultiOrgResponse {
  message?: string;
  organizations?: string[];
  requiresOrgSelection?: boolean;
  subDomain?: string;
}

type InputProps = {
  name: keyof FormData;
  label: string;
  type?: string;
  placeholder?: string;
  icon?: React.ComponentType<{ size?: string | number }>;
  required?: boolean;
};

// ---------------- Input ----------------
const Input = ({ name, label, type = "text", placeholder, icon: Icon, required = false }: InputProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormData>();

  const fieldError = errors[name];
  const error = typeof fieldError?.message === "string" ? fieldError.message : undefined;

  return (
    <div className="space-y-1.5 w-full">
      <label className="text-[13px] font-semibold text-[#344567] ml-1">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>

      <div className="relative group">
        {Icon && (
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9aa8c1] group-focus-within:text-blue-600">
            <Icon size={18} />
          </div>
        )}

        <input
          {...register(name)}
          onKeyDown={name === "email" ? preventSpaceInput : undefined}
          onInput={name === "email" ? removeSpacesFromInput : undefined}
          type={type}
          placeholder={placeholder}
          className={`w-full ${Icon ? "pl-11" : "px-4"} pr-11 py-3 bg-white border ${error ? "border-red-500" : "border-[#d9e2f0]"
            } rounded-xl text-sm text-[#0f1b35] focus:!ring-0 focus:!shadow-none focus:!outline-none ${error ? "focus:!border-red-500" : "focus:!border-[#d9e2f0]"}`}
        />
      </div>

      {/* Reserved space for error messages to prevent layout shift */}
      <p className={`text-xs text-red-500 ml-1 ${error ? "visible" : "invisible"}`}>
        {error || "\u00A0"}
      </p>
    </div>
  );
};

// ---------------- Features ----------------
const features = [
  { icon: Users, label: "Employee & Profile Management" },
  { icon: CheckSquare, label: "Task & Project Tracking" },
  { icon: Calendar, label: "Leave & Reimbursement Management" },
  { icon: Clock, label: "Timesheet & Work Hour Tracking" },
  { icon: Package, label: "Inventory & Asset Management" },
  { icon: BarChart3, label: "Shift & Roster Scheduling" },
];

// ---------------- Component ----------------
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const search = location.search;
  const { addToast } = useToaster();

  const toastShownRef = useRef(false); // Prevents duplicate toasts

  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // ---------------- Auth Check ----------------
  const checkUserLoggedIn = () => {
    const token = storageService.getItem("accessToken");

    if (token) {
      logAuthPayload(token, "existing-session");
      navigate(getPostLoginRedirectPath(token));
    } else {
      const urlToken = new URLSearchParams(search).get("token");
      const urlRefreshToken = new URLSearchParams(search).get("refresh_token") || new URLSearchParams(search).get("refreshToken");
      // Google OAuth (single-org): backend appends subDomain to the redirect URL.
      // Capture it before processing the token so refresh-token calls can use it.
      const subDomain = new URLSearchParams(search).get("subDomain") || new URLSearchParams(search).get("subdomain");
      const urlSubdomain = new URLSearchParams(search).get("subDomain") || new URLSearchParams(search).get("subdomain");
      const urlEmail = new URLSearchParams(search).get("email");

      // Store subDomain in localStorage early so it is available before token processing
      if (subDomain) {
        storageService.setItem("activeSubdomain", subDomain);
      }

      if (urlToken) {
        storageService.removeItem("isMultiOrg");
        storageService.setItem("accessToken", urlToken);
        storageService.setItem("authLoginMode", "google");

        // 1. Store refreshToken in localStorage — survives browser close/reopen and new tabs
        if (urlRefreshToken) {
          storageService.setItem("refreshToken", urlRefreshToken);
        }

        // 2. Store activeSubdomain in localStorage (URL param first, fallback to hostname)
        if (urlSubdomain) {
          storageService.setItem("activeSubdomain", urlSubdomain);
        }

        // 3. Set the Login Email if it comes through the URL
        if (urlEmail) {
          sessionStorage.setItem("loginEmail", urlEmail);
        }
        // fetchAndStoreSettings(urlToken);
        logAuthPayload(urlToken, "oauth-url-token");
        navigate(getPostLoginRedirectPath(urlToken));
      }
    }
  };


  useEffect(() => {
    checkUserLoggedIn();
  }, [search]);

  // ---------------- Submit ----------------
  const onSubmit = (data: FormData) => {
    const payload = {
      ...data,
      email: data.email.toLowerCase().trim(),
    };

    apiService
      .post("api/login", payload)
      .then((res: AxiosResponse<LoginSuccessResponse | LoginMultiOrgResponse | string>) => {
        console.log("Login response:", res);
        console.log("Login response data:", res.data);
        const responseData: LoginSuccessResponse | LoginMultiOrgResponse | string = res.data;
        const organizations =
          typeof responseData !== "string" && "organizations" in responseData
            ? responseData.organizations
            : undefined;
        const requiresOrgSelection =
          typeof responseData !== "string" && "requiresOrgSelection" in responseData
            ? responseData.requiresOrgSelection
            : false;

        if (
          typeof responseData !== "string" &&
          requiresOrgSelection &&
          Array.isArray(organizations)
        ) {
          storageService.removeItem("accessToken");
          storageService.setItem("isMultiOrg", "true");
          storageService.setItem("authLoginMode", "password");
          sessionStorage.setItem("loginEmail", data.email.trim().toLowerCase());
          sessionStorage.setItem("loginPassword", data.password);
          sessionStorage.setItem(
            "passwordOrganizations",
            JSON.stringify(organizations),
          );
          // Multi-org manual login: store subDomain in localStorage so refresh-token
          // calls after org selection can read it across tabs and browser restarts.
          const multiOrgSubDomain = (responseData as LoginMultiOrgResponse).subDomain;
          if (multiOrgSubDomain) {
            storageService.setItem("activeSubdomain", multiOrgSubDomain);
          }

          navigate("/select-organization", {
            state: {
              mode: "password-login",
              email: data.email,
              password: data.password,
              organizations,
            },
          });
          return;
        }

        const getAccessToken = (value: unknown): string | undefined => {
          if (typeof value === "string") {
            return value;
          }

          if (
            value &&
            typeof value === "object" &&
            "accessToken" in value &&
            typeof value.accessToken === "string"
          ) {
            return value.accessToken;
          }

          return undefined;
        };

        const accessToken = getAccessToken(responseData);

        if (accessToken) {
          storageService.setItem("accessToken", accessToken);

          // Store refreshToken in localStorage so it survives browser close/reopen
          // and is available across tabs — sessionStorage would be wiped on tab close.
          if (typeof responseData !== "string" && "refreshToken" in responseData && responseData.refreshToken) {
            storageService.setItem("refreshToken", responseData.refreshToken as string);
          }

          // Single-org manual login: persist subDomain in localStorage.
          // Both casings handled — backend may send "subDomain" or "subdomain".
          // apiService.ts reads "activeSubdomain" from localStorage for refresh-token calls.
          if (typeof responseData !== "string") {
            const successSubDomain =
              (responseData as LoginSuccessResponse).subDomain ||
              (responseData as LoginSuccessResponse).subdomain;
            if (successSubDomain) {
              storageService.setItem("activeSubdomain", successSubDomain);
            }
          }

          storageService.removeItem("isMultiOrg");
          storageService.setItem("authLoginMode", "password");

          // --- SET ALL CREDENTIALS IN SESSION STORAGE ---
          sessionStorage.setItem("loginEmail", data.email.trim().toLowerCase());
          sessionStorage.setItem("loginPassword", data.password); // <-- Uncommented this line
          sessionStorage.removeItem("passwordOrganizations");

          logAuthPayload(accessToken, "manual-login");
          navigate(getPostLoginRedirectPath(accessToken));
          addToast("Login Successful", "success");
        }
      })
      .catch((error) => {
        const serverMessage = error.response?.data?.message;
        if (serverMessage) {
          // Display the specific message returned by the API
          addToast(serverMessage, "error");
        } else {
          // Fallback for network errors or unexpected status codes
          addToast("An unexpected error occurred. Please try again.", "error");
        }
      });
  };

  useEffect(() => {
    const state = location.state as { authError?: string } | null;

    if (state?.authError && !toastShownRef.current) {
      toastShownRef.current = true; // Lock ref immediately
      addToast(state.authError, "error");

      // Wipe history synchronously so React Router completely forgets the state immediately
      window.history.replaceState({}, document.title);
    }
  }, [location.state, addToast]);

  return (
    // Viewport-height scroll container (html/body are overflow:hidden globally). `min-h-full`
    // lets the screen fill the viewport and scroll on short screens — no content gets clipped.
    <div className="h-dvh w-full overflow-y-auto">
      <div className="min-h-full flex flex-col overflow-hidden border border-[#dbe4f2] bg-gradient-to-br from-[#eaf1fd] via-[#f1f5fd] to-[#e6effd] shadow-[0_20px_50px_rgba(16,45,90,0.12)]">
        <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 min-h-0">

          {/* LEFT PANEL */}
          <div className="relative flex flex-col h-full overflow-hidden">

            {/* Top Content (Text & Features) */}
            <div className="px-6 pt-6 pb-8 sm:px-7 sm:pt-7 lg:px-8 lg:pt-8 flex-1 z-10">
              <div className="flex items-center gap-3 mb-5">
                <svg
                  viewBox="0 0 64 64"
                  className="w-10 h-10"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M10 8c0-3.3 2.7-6 6-6h8v44h18c3.3 0 6 2.7 6 6v6H16c-3.3 0-6-2.7-6-6V8z"
                    fill="#2563EB"
                  />
                  <rect x="28" y="12" width="14" height="14" rx="4" fill="#2563EB" />
                  <rect x="44" y="12" width="14" height="14" rx="4" fill="#2563EB" />
                  <rect x="28" y="28" width="14" height="14" rx="4" fill="#2563EB" />
                  <path d="M44 28h8l6 6v8h-8a6 6 0 0 1-6-6v-8z" fill="#2563EB" />
                </svg>
                <div>
                  <div className="text-[30px] leading-[1] font-bold text-[#172647]">Liriworks</div>
                  <div className="text-[12px] text-[#5a6b89]">All-in-one platform</div>
                </div>
              </div>

              <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] leading-[1.05] font-bold text-[#122348] max-w-[620px]">
                Manage Your  Workforce
                <br />
                Smarter
              </h1>

              <p className="mt-3 text-[16px] sm:text-[19px] lg:text-[22px] leading-[1.45] text-[#3b4f74] max-w-[650px]">
                Liriworks is a powerful  platform that helps organizations manage employees, tasks,
                timesheets, leaves, and performance - all in one place.
              </p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 max-w-[650px]">
                {features.map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white border border-[#dfe8f5] flex items-center justify-center shadow-sm flex-shrink-0">
                      <f.icon className="text-[#2b63e7] w-4 h-4" />
                    </div>
                    <span className="text-[15px] leading-tight font-semibold text-[#2b3f62]">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Image Wrapper */}
            <div className="w-full mt-auto pointer-events-none">
              <img
                src={liriworksImage}
                alt="Human Resource Management dashboard preview"
                className="w-full h-[175px] object-cover object-center block"
              />
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="px-5 py-6 lg:px-7 lg:py-7 flex items-center justify-center">
            <div className="w-full max-w-[420px] bg-white rounded-2xl border border-[#dbe3f1] shadow-[0_16px_30px_rgba(17,46,96,0.10)] p-6 lg:p-7">
              <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] leading-tight font-bold text-[#122348] mb-1">Welcome back!</h2>
              <p className="text-[15px] sm:text-[16px] lg:text-[17px] text-[#667a9a] mb-5">Sign in to your Liriworks account</p>

              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-5">

                  <Input
                    name="email"
                    label="Email address"
                    placeholder="Enter your email"
                    icon={Mail}
                    required
                  />

                  <div className="relative">
                    <Input
                      name="password"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      icon={Lock}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-[42px] text-[#8fa0bc]"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2 text-[#4e5f7d] select-none">
                      <input type="checkbox" className="rounded border-[#cbd7ea]" />
                      Remember Me
                    </label>
                    <a href="/forgot-password" className="text-[#2a62e6] font-semibold hover:underline">
                      Forgot Password?
                    </a>
                  </div>

                  <button className="w-full bg-[#2a62e6] text-white py-3 rounded-xl font-semibold hover:bg-[#2258d4] transition-colors">
                    Sign In
                  </button>
                </form>
              </FormProvider>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-[#d7dfed]" />
                <span className="text-xs text-[#8a9ab5] font-semibold">OR</span>
                <div className="flex-1 h-px bg-[#d7dfed]" />
              </div>

              {/* Social */}
              <div className="space-y-3">
                <a href={appConfig.googleLogin()}>
                  <button className="w-full border border-[#d3deef] py-2.5 rounded-xl flex justify-center gap-2 text-[#1d2d4f] font-medium hover:bg-[#f5f8fe] transition-colors">
                    <img
                      src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png"
                      className="w-5"
                      alt="Google"
                    />
                    Continue with Google
                  </button>
                </a>
              </div>

              <p className="text-center text-sm text-[#6f809d] mt-6">
                Don&apos;t have an account? <a href="/registration" className="text-[#2a62e6] font-semibold">Sign Up</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}